/**
 * Command Palette Component
 * Smart search with keyboard navigation and contextual actions
 *
 * Features:
 * - Global ⌘K / Ctrl+K shortcut
 * - Fuzzy search across all providers
 * - Grouped results by provider
 * - Contextual actions for each result
 * - Recent searches
 * - Keyboard navigation
 */

import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, CornerDownLeft, Loader2, ChevronRight, User, FileText, Compass } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '@/components/ui/command';
import {
  useCommandPaletteStore,
  useCommandPaletteState,
  useCommandPaletteActions,
} from '../store/useCommandPaletteStore';
import { providerRegistry } from '../providers/registry';
import type { SearchResult, SearchAction } from '../types';

// Import providers to register them
import '../providers';

const DEBOUNCE_MS = 300;

export function CommandPalette() {
  const navigate = useNavigate();
  const { isOpen, query, recentSearches } = useCommandPaletteState();
  const { close, setQuery, addRecentSearch, removeRecentSearch, clearRecentSearches } =
    useCommandPaletteActions();

  const [results, setResults] = useState<Map<string, SearchResult[]>>(new Map());
  const [isSearching, setIsSearching] = useState(false);
  const [selectedResult, setSelectedResult] = useState<SearchResult | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Setup global keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ⌘K or Ctrl+K to toggle
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        useCommandPaletteStore.getState().toggle();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Load default results when opened with empty query
  useEffect(() => {
    if (isOpen && !query) {
      loadDefaultResults();
    }
  }, [isOpen, query]);

  // Debounced search when query changes
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!query || query.length < 2) {
      if (!query) {
        loadDefaultResults();
      } else {
        setResults(new Map());
      }
      return;
    }

    setIsSearching(true);
    debounceRef.current = setTimeout(async () => {
      const searchResults = await providerRegistry.searchAll(query);
      setResults(searchResults);
      setIsSearching(false);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  const loadDefaultResults = async () => {
    const defaultResults = await providerRegistry.getDefaultResults();
    setResults(defaultResults);
  };

  // Navigate using react-router
  const navigateTo = useCallback(
    (path: string) => {
      close();
      setSelectedResult(null);
      navigate(path);
    },
    [close, navigate]
  );

  // Handle selecting a result - navigate directly (no secondary menu)
  const handleSelectResult = useCallback(
    (result: SearchResult) => {
      const provider = providerRegistry.get(result.providerId);
      if (!provider) return;

      const actions = provider.getActions(result.data);

      if (actions.length === 0) {
        return;
      }

      const primaryAction = actions[0]!;

      // Add to recent searches (subtitle is omitted if it's a ReactNode since it can't be serialized)
      addRecentSearch({
        resultId: result.id,
        providerId: result.providerId,
        title: result.title,
        subtitle: typeof result.subtitle === 'string' ? result.subtitle : undefined,
        path: primaryAction.path,
      });

      navigateTo(primaryAction.path);
    },
    [addRecentSearch, navigateTo]
  );

  // Handle selecting an action
  const handleSelectAction = useCallback(
    (result: SearchResult, action: SearchAction) => {
      // Add to recent searches (subtitle is omitted if it's a ReactNode since it can't be serialized)
      addRecentSearch({
        resultId: result.id,
        providerId: result.providerId,
        title: result.title,
        subtitle: typeof result.subtitle === 'string' ? result.subtitle : undefined,
        path: action.path,
      });

      navigateTo(action.path);
    },
    [addRecentSearch, navigateTo]
  );

  // Handle selecting a recent search
  const handleSelectRecent = useCallback(
    (recent: (typeof recentSearches)[0]) => {
      navigateTo(recent.path);
    },
    [navigateTo]
  );

  // Handle removing a recent search
  const handleRemoveRecent = useCallback(
    (e: React.MouseEvent, resultId: string) => {
      e.stopPropagation();
      removeRecentSearch(resultId);
    },
    [removeRecentSearch]
  );

  // Handle going back from action view
  const handleBackToSearch = useCallback(() => {
    setSelectedResult(null);
  }, []);

  // Render action items for a selected result
  const renderActions = () => {
    if (!selectedResult) return null;

    const provider = providerRegistry.get(selectedResult.providerId);
    if (!provider) return null;

    const actions = provider.getActions(selectedResult.data);

    return (
      <CommandGroup heading={`Acciones para ${selectedResult.title}`}>
        {actions.map((action) => (
          <CommandItem
            key={action.id}
            onSelect={() => handleSelectAction(selectedResult, action)}
            className="flex items-center gap-2"
          >
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <span>{action.label}</span>
            {action.shortcut && (
              <span className="ml-auto text-xs text-muted-foreground">{action.shortcut}</span>
            )}
          </CommandItem>
        ))}
        <CommandItem onSelect={handleBackToSearch} className="text-muted-foreground">
          <X className="mr-2 h-4 w-4" />
          Volver a búsqueda
        </CommandItem>
      </CommandGroup>
    );
  };

  // Map provider IDs to user-friendly names for recent searches
  const getRecentGroupName = (providerId: string): string => {
    const names: Record<string, string> = {
      doctors: 'Médicos Recientes',
      tasks: 'Trámites Recientes',
      navigation: 'Navegación Reciente',
    };
    return names[providerId] || 'Recientes';
  };

  // Get icon and color for each provider type
  const getRecentIcon = (providerId: string) => {
    switch (providerId) {
      case 'doctors':
        return (
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-100 text-teal-600 dark:bg-teal-900/50 dark:text-teal-400">
            <User className="h-4 w-4" />
          </span>
        );
      case 'tasks':
        return (
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-600 dark:bg-amber-900/50 dark:text-amber-400">
            <FileText className="h-4 w-4" />
          </span>
        );
      case 'navigation':
        return (
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400">
            <Compass className="h-4 w-4" />
          </span>
        );
      default:
        return (
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
            <FileText className="h-4 w-4" />
          </span>
        );
    }
  };

  // Render recent searches grouped by provider type
  const renderRecentSearches = () => {
    if (query || recentSearches.length === 0) return null;

    // Group recent searches by providerId
    const grouped = recentSearches.slice(0, 10).reduce(
      (acc, recent) => {
        const key = recent.providerId;
        if (!acc[key]) acc[key] = [];
        acc[key].push(recent);
        return acc;
      },
      {} as Record<string, typeof recentSearches>
    );

    // Define the order of groups
    const groupOrder = ['doctors', 'tasks', 'navigation'];
    const sortedGroups = Object.entries(grouped).sort(([a], [b]) => {
      const indexA = groupOrder.indexOf(a);
      const indexB = groupOrder.indexOf(b);
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });

    return (
      <>
        {sortedGroups.map(([providerId, items]) => (
          <CommandGroup key={`recent-${providerId}`} heading={getRecentGroupName(providerId)}>
            {items.slice(0, 3).map((recent) => (
              <CommandItem
                key={`recent-${recent.resultId}-${recent.timestamp}`}
                onSelect={() => handleSelectRecent(recent)}
                className="flex items-center gap-3 py-2"
              >
                {getRecentIcon(providerId)}
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-medium">{recent.title}</span>
                  {recent.subtitle && (
                    <span className="truncate text-xs text-muted-foreground">
                      {recent.subtitle}
                    </span>
                  )}
                </div>
                <button
                  onClick={(e) => handleRemoveRecent(e, recent.resultId)}
                  className="rounded p-1 opacity-0 transition-opacity hover:bg-accent group-data-[selected=true]:opacity-100"
                  aria-label="Eliminar de recientes"
                >
                  <X className="h-3 w-3 text-muted-foreground" />
                </button>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
        {recentSearches.length > 0 && (
          <CommandItem
            onSelect={clearRecentSearches}
            className="justify-center text-xs text-muted-foreground"
          >
            Limpiar búsquedas recientes
          </CommandItem>
        )}
        <CommandSeparator />
      </>
    );
  };

  // Render search results grouped by provider
  const renderResults = () => {
    if (selectedResult) {
      return renderActions();
    }

    const providers = providerRegistry.getAll();

    return (
      <>
        {renderRecentSearches()}
        {providers.map((provider) => {
          const providerResults = results.get(provider.id) || [];
          if (providerResults.length === 0) return null;

          return (
            <CommandGroup key={provider.id} heading={provider.name}>
              {providerResults.map((result) => {
                // Check if this result has styling metadata (from DoctorSearchProvider)
                const isPrimary = (result.data as { isPrimary?: boolean })?.isPrimary ?? true;
                const isLastInGroup =
                  (result.data as { isLastInGroup?: boolean })?.isLastInGroup ?? false;

                return (
                  <CommandItem
                    key={result.id}
                    value={`${result.title} ${result.keywords.join(' ')}`}
                    onSelect={() => handleSelectResult(result)}
                    className={cn(
                      'group relative flex items-center gap-3',
                      // Primary items (like doctor profile) - prominent styling
                      isPrimary && 'mt-1.5 rounded-lg bg-muted/40 px-4 py-3',
                      // Secondary items (sub-actions) - indented with left border
                      !isPrimary && '!ml-14 border-l-2 border-muted-foreground/20 py-2.5 !pl-4',
                      // Add spacing after last item in group
                      isLastInGroup && 'mb-4'
                    )}
                  >
                    {/* Icon with color based on type */}
                    <span
                      className={cn(
                        'flex shrink-0 items-center justify-center',
                        isPrimary &&
                          'h-10 w-10 rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400',
                        !isPrimary && 'h-5 w-5'
                      )}
                    >
                      {result.icon}
                    </span>

                    {/* Content */}
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className={cn(
                            'truncate',
                            isPrimary && 'font-semibold text-foreground',
                            !isPrimary &&
                              'text-sm text-foreground/70 group-data-[selected=true]:text-foreground'
                          )}
                        >
                          {result.title}
                        </span>
                        {/* Enter hint on selected */}
                        <span
                          className={cn(
                            'shrink-0 items-center gap-1 text-xs text-muted-foreground',
                            'opacity-0 group-data-[selected=true]:opacity-100',
                            'hidden group-data-[selected=true]:flex'
                          )}
                        >
                          <CornerDownLeft className="h-3 w-3" />
                        </span>
                      </div>
                      {result.subtitle && (
                        <div className="w-full text-xs text-muted-foreground">
                          {result.subtitle}
                        </div>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          );
        })}
      </>
    );
  };

  return (
    <CommandDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          close();
          setSelectedResult(null);
        }
      }}
      title="Búsqueda"
      description="Buscar médicos, trámites o navegar rápidamente"
      shouldFilter={!selectedResult} // Disable filtering when showing actions
    >
      <CommandInput
        placeholder="Buscar médicos, trámites, documentos... (⌘K)"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {isSearching && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Buscando...</span>
          </div>
        )}
        {!isSearching && query && results.size === 0 && (
          <CommandEmpty>No se encontraron resultados para "{query}"</CommandEmpty>
        )}
        {!isSearching && renderResults()}
      </CommandList>
    </CommandDialog>
  );
}
